'use strict';

var DEBOUNCE_MS = 400;
var LOOKUP_MAX_RETRIES = 2;
var LOOKUP_RETRY_BASE_MS = 400;
var DEFAULT_NEW_DESIGN_LABEL = 'Create from scratch';
var DEFAULT_NEW_DESIGN_PREVIEW = 'Original design';
var DEFAULT_NEW_DESIGN_PREVIEW_MSG = 'Your item will be created from scratch.';
var DEFAULT_NEW_DESIGN_WHEN = 'always';
var DEFAULT_LAYOUT = 'carousel';
var DESIGN_SOURCE_KEY = 'Design Source';
var DESIGN_SOURCE_SCRATCH = 'Create from scratch';
var DESIGN_SOURCE_CATALOG = 'Catalog';
var DESIGN_FILE_KEY = 'Design File';
var CART_KEY_ENTRY_ID = '_design_library_entry_id';
var CART_KEY_PREVIEW_URL = '_design_preview_url';
var CART_KEY_PRODUCTION_URL = '_design_production_url';
var CART_KEY_PRODUCTION_FILES = '_design_production_files';
/** MNGSD-2 D3 — what the design was matched on, e.g. `name=joseph;shape=round`. */
var CART_KEY_MATCH = '_design_match';
var DESIGN_LIBRARY_CART_KEYS = [
  CART_KEY_ENTRY_ID,
  CART_KEY_PREVIEW_URL,
  CART_KEY_PRODUCTION_URL,
  CART_KEY_PRODUCTION_FILES,
  CART_KEY_MATCH,
  DESIGN_SOURCE_KEY,
  DESIGN_FILE_KEY
];

function splitDependsOn(value) {
  return String(value || '')
    .split(',')
    .map(function (s) { return s.trim(); })
    .filter(Boolean);
}

function parseDefaultOrderProps(value) {
  var out = {};
  if (value == null) return out;

  if (typeof value === 'object' && !Array.isArray(value)) {
    Object.keys(value).forEach(function (key) {
      var label = String(key).trim();
      var val = value[key];
      if (!label || val == null || String(val).trim() === '') return;
      out[label] = String(val).trim();
    });
    return out;
  }

  String(value).split(',').forEach(function (pair) {
    pair = pair.trim();
    if (!pair) return;
    var eq = pair.indexOf('=');
    if (eq === -1) return;
    var label = pair.slice(0, eq).trim();
    var val = pair.slice(eq + 1).trim();
    if (label && val) out[label] = val;
  });
  return out;
}

/**
 * ENTW-ONE — cap how many catalog designs the shopper is offered.
 *
 * `max_designs: N` on the Design Library field setting. Entwine has up to 35 published
 * designs for a single initials combination (avg 4 across 320 combinations), which reads
 * as a wall of near-identical thumbnails. Capping is a DISPLAY rule on purpose: the
 * catalog keeps every design for artists, and "newest wins" stays true automatically as
 * new designs are published — nothing to re-maintain.
 *
 * 0 / unset = unlimited (every other family keeps today's behaviour).
 * The resolver already returns matches newest-first, so slicing keeps the newest.
 */
var DEFAULT_MAX_DESIGNS = 0;
var MAX_DESIGNS_HARD_CAP = 32;

/**
 * MNGOPT-2 — designs on the shapes the shopper did NOT pick.
 *
 * 84% of catalog names are drawn on exactly one shape, so a shopper's chosen shape has a design
 * only ~21% of the time. Off by default: every other family keeps today's behaviour, and a field
 * opts in with `show_other_shapes: 1`.
 */
var DEFAULT_SHOW_OTHER_SHAPES = false;
/** At most one design per other shape, and at most this many shapes (grill D3). */
var OTHER_SHAPES_MAX = 5;
/**
 * Which personalization field carries the shape. Tapping an other-shape design moves the shopper
 * to that shape, and this is the only thing that says which field to move.
 */
var DEFAULT_SHAPE_FIELD = 'Shape';

/** `1` / `true` / `yes` / `on` all read as on; anything else, including absent, is off. */
function parseBooleanSetting(raw) {
  if (raw === true) return true;
  if (raw == null || raw === false) return false;
  var v = String(raw).trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
}

function parseMaxDesigns(raw) {
  if (raw == null) return DEFAULT_MAX_DESIGNS;
  var n = parseInt(String(raw).trim(), 10);
  if (!isFinite(n) || isNaN(n) || n <= 0) return DEFAULT_MAX_DESIGNS;
  return n > MAX_DESIGNS_HARD_CAP ? MAX_DESIGNS_HARD_CAP : n;
}

/**
 * Returns a response narrowed to at most `settings.max_designs` summaries.
 * `options.keepEntryId` pins an already-chosen design (cart edit) so re-opening a saved
 * line never silently drops the design the customer actually bought.
 */
function applyDesignLibraryDisplayCap(response, settings, options) {
  if (!response || response.ok === false) return response;
  var cap = parseMaxDesigns(settings && settings.max_designs);
  var summaries = Array.isArray(response.matchSummaries) ? response.matchSummaries : [];
  if (cap <= 0 || summaries.length <= cap) return response;

  var keepEntryId = options && options.keepEntryId != null ? String(options.keepEntryId) : '';
  var kept = summaries.slice(0, cap);

  if (keepEntryId) {
    var alreadyKept = false;
    for (var i = 0; i < kept.length; i += 1) {
      if (kept[i] && String(kept[i].entryId) === keepEntryId) { alreadyKept = true; break; }
    }
    if (!alreadyKept) {
      for (var j = 0; j < summaries.length; j += 1) {
        if (summaries[j] && String(summaries[j].entryId) === keepEntryId) {
          // Pinned design takes the last slot so the newest still leads.
          kept = kept.slice(0, Math.max(0, cap - 1)).concat([summaries[j]]);
          break;
        }
      }
    }
  }

  var out = {};
  for (var k in response) { if (Object.prototype.hasOwnProperty.call(response, k)) out[k] = response[k]; }
  out.matchSummaries = kept;
  out.totalMatches = kept.length;
  return out;
}

function normalizeShapeValue(value) {
  return String(value == null ? '' : value).trim().toLowerCase();
}

/**
 * The shape's name with any trailing note dropped — `Diamond (Popular)` and `diamond` are one
 * shape. The catalog and the shape field word them differently, so every comparison between the
 * two has to go through here.
 */
function canonicalShapeName(value) {
  return normalizeShapeValue(value).replace(/\s*\([^)]*\)\s*$/, '').trim();
}

/**
 * MNGSD-2 D3 — what the design was matched on, as one short line for the order line.
 *
 * `name=joseph;shape=round`. The shape is the DESIGN's own, as the server stamped it — never the
 * field's value. That is the point: the order then says what the design IS, so anyone reading it
 * later (the intake net, an audit, a person) can compare it with the shape the shopper ordered
 * without opening the catalog. A design whose shape is unknown writes no shape at all rather than
 * borrowing the field's and pretending.
 */
function formatDesignMatch(orderProps, options) {
  options = options || {};
  var shapeKey = String(options.shapeField || DEFAULT_SHAPE_FIELD).trim().toLowerCase().replace(/\s+/g, '_');
  var designShape = canonicalShapeName(options.designShape);
  var keys = Object.keys(orderProps || {}).sort(function (a, b) {
    var x = String(a).toLowerCase();
    var y = String(b).toLowerCase();
    return x < y ? -1 : x > y ? 1 : 0;
  });
  var parts = [];
  for (var i = 0; i < keys.length; i++) {
    var key = String(keys[i]).trim().toLowerCase().replace(/\s+/g, '_');
    var raw = orderProps[keys[i]];
    var val = String(raw == null ? '' : raw).trim().toLowerCase().replace(/\s+/g, ' ');
    if (key === shapeKey) {
      if (!designShape) continue;
      val = designShape;
    }
    if (!key || !val) continue;
    parts.push(key + '=' + val);
  }
  return parts.join(';');
}

/**
 * MNGOPT-2 — what the "Show other shapes" row actually offers.
 *
 * The server returns at most one design per other shape but does not know which shapes are on
 * sale, so the allow-list (when a merchant sets one) and the display cap live here. Order is the
 * server's — newest design first — so the cap keeps the freshest shapes.
 */
function selectOtherShapeOptions(response, settings) {
  if (!response || response.ok === false) return [];
  if (!settings || settings.show_other_shapes !== true) return [];
  var rows = Array.isArray(response.otherShapeSummaries) ? response.otherShapeSummaries : [];
  if (!rows.length) return [];

  var allow = Array.isArray(settings.other_shapes_allow) ? settings.other_shapes_allow : [];
  var allowed = null;
  if (allow.length) {
    allowed = {};
    for (var a = 0; a < allow.length; a++) allowed[normalizeShapeValue(allow[a])] = true;
  }

  var out = [];
  var seen = {};
  for (var i = 0; i < rows.length && out.length < OTHER_SHAPES_MAX; i++) {
    var row = rows[i];
    if (!row) continue;
    var shape = normalizeShapeValue(row.shape);
    /* No shape means nothing to switch the shopper to, so it is not an alternative. */
    if (!shape || seen[shape]) continue;
    if (allowed && !allowed[shape]) continue;
    if (!row.previewUrl) continue;
    seen[shape] = true;
    out.push(row);
  }
  return out;
}

/** Title-case a normalized shape for display — `round` reads as `Round` on the card. */
function formatShapeLabel(shape) {
  var v = normalizeShapeValue(shape);
  if (!v) return '';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function parseDesignLibrarySettings(raw) {
  var out = {
    depends_on: [],
    default_order_props: {},
    preferred_page_labels: [],
    production_page_labels: [],
    layout: DEFAULT_LAYOUT,
    new_design_label: DEFAULT_NEW_DESIGN_LABEL,
    new_design_preview: DEFAULT_NEW_DESIGN_PREVIEW,
    new_design_preview_message: DEFAULT_NEW_DESIGN_PREVIEW_MSG,
    new_design_when: DEFAULT_NEW_DESIGN_WHEN,
    max_designs: DEFAULT_MAX_DESIGNS,
    show_other_shapes: DEFAULT_SHOW_OTHER_SHAPES,
    other_shapes_allow: [],
    shape_field: DEFAULT_SHAPE_FIELD
  };
  if (raw == null) return out;

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    if (typeof raw.value === 'string' && raw.value.trim()) {
      return parseDesignLibrarySettings(raw.value);
    }
    if (raw.depends_on != null) {
      out.depends_on = splitDependsOn(raw.depends_on);
    }
    if (raw.default_order_props != null) {
      out.default_order_props = parseDefaultOrderProps(raw.default_order_props);
    }
    if (raw.preferred_page_labels != null) {
      out.preferred_page_labels = splitDependsOn(raw.preferred_page_labels);
    } else if (raw.preferredPageLabels != null) {
      out.preferred_page_labels = Array.isArray(raw.preferredPageLabels)
        ? raw.preferredPageLabels.map(function (s) { return String(s).trim(); }).filter(Boolean)
        : splitDependsOn(raw.preferredPageLabels);
    }
    if (raw.production_page_labels != null) {
      out.production_page_labels = splitDependsOn(raw.production_page_labels);
    } else if (raw.productionPageLabels != null) {
      out.production_page_labels = Array.isArray(raw.productionPageLabels)
        ? raw.productionPageLabels.map(function (s) { return String(s).trim(); }).filter(Boolean)
        : splitDependsOn(raw.productionPageLabels);
    }
    if (raw.layout) out.layout = String(raw.layout).trim() || DEFAULT_LAYOUT;
    if (raw.new_design_label) out.new_design_label = String(raw.new_design_label).trim() || DEFAULT_NEW_DESIGN_LABEL;
    if (raw.new_design_preview) out.new_design_preview = String(raw.new_design_preview).trim() || DEFAULT_NEW_DESIGN_PREVIEW;
    if (raw.new_design_preview_message) out.new_design_preview_message = String(raw.new_design_preview_message).trim() || DEFAULT_NEW_DESIGN_PREVIEW_MSG;
    if (raw.new_design_when) out.new_design_when = String(raw.new_design_when).trim() || DEFAULT_NEW_DESIGN_WHEN;
    if (raw.max_designs != null) out.max_designs = parseMaxDesigns(raw.max_designs);
    if (raw.show_other_shapes != null) out.show_other_shapes = parseBooleanSetting(raw.show_other_shapes);
    if (raw.other_shapes_allow != null) out.other_shapes_allow = splitDependsOn(raw.other_shapes_allow);
    if (raw.shape_field != null) out.shape_field = String(raw.shape_field).trim() || DEFAULT_SHAPE_FIELD;
    return out;
  }

  var text = String(raw).trim();
  if (!text) return out;
  if (text.charAt(0) === '{') {
    try {
      var parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object') return parseDesignLibrarySettings(parsed);
    } catch (e) {}
  }

  var lines = text.indexOf('\n') >= 0 ? text.split(/\r?\n/) : [text];
  lines.forEach(function (line) {
    line = line.trim();
    if (!line || line.indexOf(':') === -1) return;
    var idx = line.indexOf(':');
    var key = line.slice(0, idx).trim().toLowerCase().replace(/\s+/g, '_');
    var val = line.slice(idx + 1).trim();
    if (key === 'depends_on') out.depends_on = splitDependsOn(val);
    else if (key === 'default_order_props') out.default_order_props = parseDefaultOrderProps(val);
    else if (key === 'preferred_page_labels') out.preferred_page_labels = splitDependsOn(val);
    else if (key === 'production_page_labels') out.production_page_labels = splitDependsOn(val);
    else if (key === 'layout') out.layout = val || DEFAULT_LAYOUT;
    else if (key === 'new_design_label') out.new_design_label = val || DEFAULT_NEW_DESIGN_LABEL;
    else if (key === 'new_design_preview') out.new_design_preview = val || DEFAULT_NEW_DESIGN_PREVIEW;
    else if (key === 'new_design_preview_message') out.new_design_preview_message = val || DEFAULT_NEW_DESIGN_PREVIEW_MSG;
    else if (key === 'new_design_when') out.new_design_when = val || DEFAULT_NEW_DESIGN_WHEN;
    else if (key === 'max_designs') out.max_designs = parseMaxDesigns(val);
    else if (key === 'show_other_shapes') out.show_other_shapes = parseBooleanSetting(val);
    else if (key === 'other_shapes_allow') out.other_shapes_allow = splitDependsOn(val);
    else if (key === 'shape_field') out.shape_field = val || DEFAULT_SHAPE_FIELD;
  });

  if (!out.depends_on.length && /depends_on\s*:/i.test(String(raw))) {
    var match = String(raw).match(/depends_on\s*:\s*([^\n]+)/i);
    if (match) out.depends_on = splitDependsOn(match[1]);
  }
  return out;
}

function selectionValue(selections, label) {
  if (!selections || label == null) return '';
  /* PB-SBT-1 — shared seam (pb-builder-tokens) resolves bundle-scoped keys when present. */
  var tokens = typeof globalThis !== 'undefined' ? globalThis.PBBuilderTokens : null;
  var val = tokens && typeof tokens.lookupSelectionValue === 'function'
    ? tokens.lookupSelectionValue(selections, label)
    : selections[label];
  if (val == null) return '';
  return String(val).trim();
}

function buildOrderProps(dependsOnLabels, selections, defaultOrderProps) {
  var labels = Array.isArray(dependsOnLabels) ? dependsOnLabels : [];
  var defaults = defaultOrderProps && typeof defaultOrderProps === 'object' ? defaultOrderProps : {};
  var orderProps = {};

  for (var i = 0; i < labels.length; i++) {
    var label = labels[i];
    var customerVal = selectionValue(selections, label);
    if (customerVal) {
      orderProps[label] = customerVal;
      continue;
    }
    if (defaults[label] != null && String(defaults[label]).trim() !== '') {
      orderProps[label] = String(defaults[label]).trim();
      continue;
    }
    return { complete: false, orderProps: null };
  }

  Object.keys(defaults).forEach(function (defaultLabel) {
    if (orderProps[defaultLabel] != null) return;
    var customerVal = selectionValue(selections, defaultLabel);
    orderProps[defaultLabel] = customerVal || String(defaults[defaultLabel]).trim();
  });

  return { complete: true, orderProps: orderProps };
}

function buildLookupRequest(shopifyProductId, orderProps, preferredPageLabels, productionPageLabels, options) {
  var request = {
    shopifyProductId: String(shopifyProductId || ''),
    orderProps: orderProps || {}
  };
  var labels = Array.isArray(preferredPageLabels)
    ? preferredPageLabels.map(function (s) { return String(s).trim(); }).filter(Boolean)
    : [];
  if (labels.length) request.preferredPageLabels = labels;
  var productionLabels = Array.isArray(productionPageLabels)
    ? productionPageLabels.map(function (s) { return String(s).trim(); }).filter(Boolean)
    : [];
  if (productionLabels.length) request.productionPageLabels = productionLabels;
  /* MNGOPT-2 — only sent when the field opted in, so every other product's request is unchanged. */
  if (options && options.includeOtherShapes === true) request.includeOtherShapes = true;
  return request;
}

/**
 * PBPF-2 — D2: which product's production label list wins, first hit wins.
 * Product metafield (`custom.pb_production_page_labels`) beats the field setting's
 * `production_page_labels:` default. Neither set → empty list → theme sends nothing,
 * server returns nothing, cart line is byte-identical to today (D10).
 */
function resolveProductionPageLabels(metafieldLabels, settingsLabels) {
  var fromMetafield = Array.isArray(metafieldLabels)
    ? metafieldLabels.map(function (s) { return String(s).trim(); }).filter(Boolean)
    : [];
  if (fromMetafield.length) return fromMetafield;
  return Array.isArray(settingsLabels)
    ? settingsLabels.map(function (s) { return String(s).trim(); }).filter(Boolean)
    : [];
}

/**
 * PBPF-2 — D1: one stacked line per configured production label, in configured order.
 * A resolved label is the bare file URL; an unresolved one reads `MISSING — <label>` so
 * the gap is visible on the monday.com item instead of an empty column (locked decision 6).
 */
function formatProductionFilesBlock(productionFiles) {
  if (!Array.isArray(productionFiles) || !productionFiles.length) return '';
  return productionFiles.map(function (item) {
    var label = item && item.label != null ? String(item.label).trim() : '';
    var url = item && item.url != null ? String(item.url).trim() : '';
    return url || ('MISSING — ' + label);
  }).join('\n');
}

function decideStepVisibility(response) {
  if (!response) return 'skip';
  return 'show';
}

/** MNGOPT-2 — true when the reply carries at least one usable design on another shape. */
function hasOtherShapeOptions(response) {
  return !!(
    response &&
    response.ok !== false &&
    Array.isArray(response.otherShapeSummaries) &&
    response.otherShapeSummaries.length
  );
}

function isScratchOnlyResponse(response) {
  if (!response) return false;
  if (response.ok === false) return true;
  /* MNGOPT-2 — alternatives on other shapes are real designs to offer, so a reply carrying them
     is not "scratch only" even when the shopper's own shape matched nothing. */
  if (hasOtherShapeOptions(response)) return false;
  return typeof response.totalMatches !== 'number' || response.totalMatches < 1;
}

function shouldShowNewDesign(settings, response) {
  var when = (settings && settings.new_design_when) || DEFAULT_NEW_DESIGN_WHEN;
  if (when === 'no_match_only') {
    return isScratchOnlyResponse(response);
  }
  return true;
}

function getCatalogMatchCount(response) {
  if (!response || response.ok === false) return 0;
  if (typeof response.totalMatches === 'number') return response.totalMatches;
  return Array.isArray(response.matchSummaries) ? response.matchSummaries.length : 0;
}

/** Preview-only when one deterministic catalog match under no_match_only (no picker row). */
function shouldShowCatalogPicker(settings, response) {
  if (isScratchOnlyResponse(response)) return true;
  var matchCount = getCatalogMatchCount(response);
  if (matchCount <= 0) return false;
  if (matchCount === 1 && !shouldShowNewDesign(settings, response)) return false;
  return true;
}

function shouldAutoSwitchCartEditOnDepsChange(settings) {
  return ((settings && settings.new_design_when) || DEFAULT_NEW_DESIGN_WHEN) === 'no_match_only';
}

function shouldPreserveCartEditSelectionOnLookup(settings, options) {
  options = options || {};
  if (options.cartEditDepsChanged && shouldAutoSwitchCartEditOnDepsChange(settings)) {
    return false;
  }
  return !!options.preserveSelection;
}

function resolveNewDesignLabelForRestore(snapshot, settings) {
  if (snapshot && snapshot.savedLabelText && String(snapshot.savedLabelText).trim()) {
    return String(snapshot.savedLabelText).trim();
  }
  return (settings && settings.new_design_label) || DEFAULT_NEW_DESIGN_LABEL;
}

function cloneFixture(fixture) {
  return JSON.parse(JSON.stringify(fixture));
}

var DEFAULT_PROXY_PATH = '/apps/pp-design-library/resolve';

function normalizeProxyPath(path) {
  var raw = String(path || '').trim();
  if (!raw) return DEFAULT_PROXY_PATH;
  if (raw.indexOf('http://') === 0 || raw.indexOf('https://') === 0) return raw;
  if (raw.charAt(0) !== '/') raw = '/' + raw;
  return raw;
}

function shouldUseMockDesignLibrary(options) {
  options = options || {};
  if (options.forceMock === true) return true;
  if (options.useMock === true) return true;
  if (options.useMock === false) return false;
  return false;
}

function parseResolveResponseBody(body) {
  if (!body || typeof body !== 'object') {
    return {
      ok: false,
      error: 'invalid_response',
      message: 'Design lookup returned an invalid response'
    };
  }
  if (body.ok === false) {
    return {
      ok: false,
      error: body.error || 'upstream_unavailable',
      message: body.message || 'Design lookup temporarily unavailable'
    };
  }
  var summaries = Array.isArray(body.matchSummaries) ? body.matchSummaries : [];
  var totalMatches = typeof body.totalMatches === 'number' ? body.totalMatches : summaries.length;
  var out = {
    ok: true,
    totalMatches: totalMatches,
    matchSummaries: summaries.slice(0, 32)
  };
  /* MNGOPT-2 — absent unless the server was asked, so an un-opted field sees today's shape. */
  if (Array.isArray(body.otherShapeSummaries)) {
    out.otherShapeSummaries = body.otherShapeSummaries.slice(0, 32);
  }
  /* MNGOPT-10 — every shape's designs, so a shape change is answered from this reply. */
  if (Array.isArray(body.allShapeSummaries)) {
    out.allShapeSummaries = body.allShapeSummaries.slice(0, 64);
  }
  return out;
}

function isAbortError(err) {
  if (!err) return false;
  if (err.name === 'AbortError' || err.code === 20) return true;
  if (typeof DOMException !== 'undefined' && err instanceof DOMException && err.name === 'AbortError') return true;
  return false;
}

function isLookupCancellationError(err) {
  if (isAbortError(err)) return true;
  if (!err) return false;
  var reason = err.reason != null ? String(err.reason) : '';
  var msg = String(err.message != null ? err.message : err);
  if (reason === 'reset' || reason === 'superseded') return true;
  if (msg === 'reset' || msg === 'superseded') return true;
  if (/design lookup cancelled/i.test(msg)) return true;
  return false;
}

function isExpectedLookupAbort(state, err) {
  if (isLookupCancellationError(err)) return true;
  if (
    state &&
    (state.lookupAbortReason === 'superseded' || state.lookupAbortReason === 'reset')
  ) {
    return true;
  }
  if (state && state.abortController && state.abortController.signal && state.abortController.signal.aborted) return true;
  return false;
}

function cancelInFlightLookup(state, reason) {
  if (!state) return;
  var abortReason = reason || 'superseded';
  state.lookupAbortReason = abortReason;
  if (state.lookupPromise && typeof state.lookupPromise.catch === 'function') {
    state.lookupPromise.catch(function () {});
    state.lookupPromise = null;
  }
  if (state.abortController && typeof state.abortController.abort === 'function') {
    try {
      state.abortController.abort(abortReason);
    } catch (e) {
      try {
        state.abortController.abort();
      } catch (e2) {}
    }
  }
  state.abortController = null;
}

function isTransientLookupFailure(response) {
  if (!response || response.ok !== false) return false;
  if (response.error === 'aborted' || response.error === 'invalid_request') return false;
  return true;
}

function beginLookupGeneration(state) {
  if (!state) return 0;
  cancelInFlightLookup(state, 'superseded');
  state.lookupGeneration = (state.lookupGeneration || 0) + 1;
  state.lookupAbortReason = null;
  state.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
  return state.lookupGeneration;
}

function isStaleLookupGeneration(state, generation) {
  return !state || state.lookupGeneration !== generation;
}

function hasCachedLookupResult(state, nextKey) {
  if (!state || state.loading) return false;
  if (!nextKey || state.orderPropsKey !== nextKey) return false;
  if (!state.response) return false;
  return !!(state.skipped || state.prefetchedReady || state.shown);
}

function needsDesignLibraryLookup(state, gate, nextKey) {
  if (!gate || !gate.complete) return false;
  if (state && state.loading) return false;
  if (hasCachedLookupResult(state, nextKey)) return false;
  if (!state || !state.orderPropsKey || state.orderPropsKey !== nextKey) return true;
  if (!state.response) return true;
  return false;
}

function shouldScheduleDesignLibraryLookup(state, gate, nextKey, isActiveStep) {
  if (!isActiveStep) return false;
  return needsDesignLibraryLookup(state, gate, nextKey);
}

function shouldScheduleDesignLibraryPrefetch(state, gate, nextKey) {
  return needsDesignLibraryLookup(state, gate, nextKey);
}

function delayMs(ms) {
  return new Promise(function (resolve) {
    setTimeout(resolve, ms);
  });
}

function resolveDesignLibrary(request, options) {
  options = options || {};
  if (!request || !request.shopifyProductId) {
    return Promise.resolve({
      ok: false,
      error: 'invalid_request',
      message: 'Missing shopifyProductId'
    });
  }

  if (shouldUseMockDesignLibrary(options)) {
    return mockResolveDesignLibrary(request, options);
  }

  var fetchFn = options.fetch || (typeof globalThis !== 'undefined' && globalThis.fetch);
  if (!fetchFn) {
    return Promise.resolve({
      ok: false,
      error: 'upstream_unavailable',
      message: 'Design lookup temporarily unavailable'
    });
  }

  var url = normalizeProxyPath(options.proxyPath);
  var payload = {
    shopifyProductId: String(request.shopifyProductId),
    orderProps: request.orderProps || {}
  };
  if (Array.isArray(request.preferredPageLabels) && request.preferredPageLabels.length) {
    payload.preferredPageLabels = request.preferredPageLabels;
  }
  if (Array.isArray(request.productionPageLabels) && request.productionPageLabels.length) {
    payload.productionPageLabels = request.productionPageLabels;
  }
  /* MNGOPT-2 — absent unless the field opted in; the server treats absent as today's behaviour. */
  if (request.includeOtherShapes === true) payload.includeOtherShapes = true;
  var fetchOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(payload)
  };
  if (options.signal) fetchOptions.signal = options.signal;

  return fetchFn(url, fetchOptions).then(function (res) {
    if (!res || !res.ok) {
      return {
        ok: false,
        error: 'upstream_unavailable',
        message: 'Design lookup temporarily unavailable'
      };
    }
    return res.json().catch(function () {
      return {
        ok: false,
        error: 'invalid_response',
        message: 'Design lookup returned an invalid response'
      };
    });
  }).then(function (body) {
    return parseResolveResponseBody(body);
  }).catch(function (err) {
    if (options.signal && options.signal.aborted) {
      return {
        ok: false,
        error: 'aborted',
        message: 'Design lookup cancelled'
      };
    }
    if (isLookupCancellationError(err)) {
      return {
        ok: false,
        error: 'aborted',
        message: 'Design lookup cancelled'
      };
    }
    return {
      ok: false,
      error: 'upstream_unavailable',
      message: 'Design lookup temporarily unavailable'
    };
  });
}

function resolveDesignLibraryWithRetry(request, options) {
  options = options || {};
  var maxRetries = typeof options.maxRetries === 'number' ? options.maxRetries : LOOKUP_MAX_RETRIES;
  var attempt = 0;

  function runAttempt() {
    return resolveDesignLibrary(request, options).then(function (response) {
      if (response && response.ok === true) return response;
      if (response && response.error === 'aborted') return response;
      if (options.signal && options.signal.aborted) return response;
      if (attempt >= maxRetries || !isTransientLookupFailure(response)) return response;
      attempt += 1;
      return delayMs(LOOKUP_RETRY_BASE_MS * Math.pow(2, attempt - 1)).then(function () {
        if (options.signal && options.signal.aborted) {
          return {
            ok: false,
            error: 'aborted',
            message: 'Design lookup cancelled'
          };
        }
        return runAttempt();
      });
    });
  }

  return runAttempt();
}

function mockResolveDesignLibrary(request, options) {
  options = options || {};
  if (options.signal && options.signal.aborted) {
    return Promise.resolve({
      ok: false,
      error: 'aborted',
      message: 'Design lookup cancelled'
    });
  }
  if (!request || !request.shopifyProductId) {
    return Promise.resolve({
      ok: false,
      error: 'invalid_request',
      message: 'Missing shopifyProductId'
    });
  }

  if (options.fixture) {
    return Promise.resolve(cloneFixture(options.fixture));
  }

  var name = request.orderProps && request.orderProps.Name ? String(request.orderProps.Name) : '';
  var shape = request.orderProps && request.orderProps.Shape ? String(request.orderProps.Shape) : '';
  if (name === '__nomatch__' || name === '__zero__' || shape === '__EMPTY__') {
    return Promise.resolve({ ok: true, totalMatches: 0, matchSummaries: [] });
  }
  if (name === '__error__') {
    return Promise.resolve({
      ok: false,
      error: 'upstream_unavailable',
      message: 'Design lookup temporarily unavailable'
    });
  }
  if (name === '__single__') {
    var singleLabel = [name, shape].filter(Boolean).join(' · ') || 'Catalog design';
    return Promise.resolve({
      ok: true,
      totalMatches: 1,
      matchSummaries: [
        {
          entryId: 'dl_entry_mock_single',
          previewUrl: 'https://cdn.shopify.com/s/files/1/0000/0001/files/pb-dl-mock-preview-single.png',
          productionUrl: 'https://cdn.shopify.com/s/files/1/0000/0001/files/pb-dl-mock-production-single.png',
          label: singleLabel
        }
      ]
    });
  }

  var label = [name, shape].filter(Boolean).join(' · ') || 'Catalog design';
  return Promise.resolve({
    ok: true,
    totalMatches: 2,
    matchSummaries: [
      {
        entryId: 'dl_entry_mock_1',
        previewUrl: 'https://cdn.shopify.com/s/files/1/0000/0001/files/pb-dl-mock-preview-1.png',
        productionUrl: 'https://cdn.shopify.com/s/files/1/0000/0001/files/pb-dl-mock-production-1.png',
        label: label + ' A'
      },
      {
        entryId: 'dl_entry_mock_2',
        previewUrl: 'https://cdn.shopify.com/s/files/1/0000/0001/files/pb-dl-mock-preview-2.png',
        productionUrl: 'https://cdn.shopify.com/s/files/1/0000/0001/files/pb-dl-mock-production-2.png',
        label: label + ' B'
      }
    ]
  });
}

function escapeAttr(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function isPlainPreviewText(value) {
  var text = String(value == null ? '' : value).trim();
  if (!text) return false;
  if (/^https?:\/\//i.test(text)) return false;
  if (text.indexOf('<') >= 0) return false;
  return true;
}

function resolveNewDesignPreviewCopy(settings, fieldMeta) {
  settings = settings || {};
  fieldMeta = fieldMeta || {};
  var preview = String(settings.new_design_preview || '').trim();
  var msg = String(settings.new_design_preview_message || '').trim();
  if (!preview && isPlainPreviewText(fieldMeta.visual)) preview = String(fieldMeta.visual).trim();
  if (!preview) preview = DEFAULT_NEW_DESIGN_PREVIEW;
  if (!msg && fieldMeta.description) msg = String(fieldMeta.description).trim();
  if (!msg) msg = DEFAULT_NEW_DESIGN_PREVIEW_MSG;
  return { preview: preview, msg: msg };
}

function normalizeDesignLibraryLayout(layout) {
  var value = String(layout || '').trim().toLowerCase();
  if (!value || value === 'grid_two_row_scroll') return 'carousel';
  return value;
}

function renderScratchThumbnail(newDesignLabel) {
  var label = escapeAttr(newDesignLabel || DEFAULT_NEW_DESIGN_LABEL);
  return (
    '<button type="button" class="pb-design-card pb-design-card--scratch" data-selection-mode="new_design" aria-label="' + label + '" aria-pressed="false">' +
      '<span class="pb-dl-scratch-label">' + label + '</span>' +
    '</button>'
  );
}

function renderCatalogThumbnail(item) {
  var entryId = escapeAttr(item.entryId || '');
  var previewUrl = escapeAttr(item.previewUrl || '');
  var cardLabel = escapeAttr(item.label || 'Design option');
  return (
    '<button type="button" class="pb-design-card pb-design-card--catalog" data-entry-id="' + entryId + '" data-preview-url="' + previewUrl + '" data-label="' + cardLabel + '" aria-label="' + cardLabel + '">' +
      '<img src="' + previewUrl + '" alt="" loading="lazy">' +
    '</button>'
  );
}

/**
 * MNGOPT-4 — the divider that introduces the other-shape designs.
 *
 * Plain text, not a control: the alternatives are always visible, exactly like the designs for the
 * shopper's own shape. It rides in the same carousel track so the label scrolls with the cards it
 * introduces instead of drifting away from them.
 */
function renderOtherShapesLabel(count) {
  if (!count) return '';
  return (
    '<span class="pb-dl-other-shapes-label" aria-hidden="true">' +
      '<span class="pb-dl-other-shapes-label-text">' +
        '<span class="pb-dl-other-shapes-label-line">Other</span>' +
        '<span class="pb-dl-other-shapes-label-line">Shapes</span>' +
      '</span>' +
    '</span>'
  );
}

/**
 * MNGOPT-6 — match a catalog shape to the shape field's own option value.
 *
 * They are not the same string. The catalog stores `diamond`; the field offers
 * `Diamond (Popular)`. Comparing them exactly meant that option could never be found, so tapping
 * a diamond design silently did nothing — the shopper saw a card that would not respond.
 *
 * Compares on the shape name with any trailing parenthetical note dropped, so a merchant can
 * annotate an option without breaking the tap.
 */
function shapeMatchesOptionValue(shape, optionValue) {
  var a = canonicalShapeName(shape);
  var b = canonicalShapeName(optionValue);
  return !!a && a === b;
}

/**
 * A design from another shape. Carries `data-shape` because tapping it moves the shopper to that
 * shape — the card is the only place that value is known at click time.
 */
function renderOtherShapeThumbnail(item) {
  var shapeLabel = formatShapeLabel(item && item.shape);
  var cardLabel = shapeLabel || 'Other shape';
  return (
    '<button type="button" class="pb-design-card pb-design-card--catalog pb-design-card--other-shape" ' +
      'data-entry-id="' + escapeAttr(item.entryId || '') + '" ' +
      'data-preview-url="' + escapeAttr(item.previewUrl || '') + '" ' +
      'data-shape="' + escapeAttr(item.shape || '') + '" ' +
      'data-label="' + escapeAttr(item.label || cardLabel) + '" ' +
      'aria-label="' + escapeAttr(cardLabel) + '">' +
      '<img src="' + escapeAttr(item.previewUrl || '') + '" alt="" loading="lazy">' +
      '<span class="pb-dl-other-shape-tag">' + escapeAttr(shapeLabel) + '</span>' +
    '</button>'
  );
}

function renderThumbnailGrid(summaries, options) {
  options = options || {};
  var newDesignLabel = options.newDesignLabel || DEFAULT_NEW_DESIGN_LABEL;
  var showNewDesign = options.showNewDesign !== false;
  var scratch = showNewDesign ? renderScratchThumbnail(newDesignLabel) : '';
  var catalog = (summaries || []).map(renderCatalogThumbnail).join('');

  /* MNGOPT-2 — nothing below this point renders unless the field opted in and the server
     returned alternatives, so every other product's grid is byte-identical to before. */
  var others = Array.isArray(options.otherShapes) ? options.otherShapes : [];
  if (!others.length) return scratch + catalog;

  /* MNGOPT-4 — always shown, behind a text label. They read as designs the shopper can pick,
     the same as the ones for their own shape, rather than something hidden behind a control. */
  return scratch + catalog + renderOtherShapesLabel(others.length) +
    others.map(renderOtherShapeThumbnail).join('');
}

function getFirstMatchSummary(summaries) {
  if (!Array.isArray(summaries) || !summaries.length) return null;
  return summaries[0] || null;
}

function orderPropsKey(orderProps) {
  var keys = Object.keys(orderProps || {}).sort();
  return keys.map(function (k) { return k + '=' + orderProps[k]; }).join('|');
}

/**
 * MNGOPT-10 — what identifies a cached lookup.
 *
 * With `show_other_shapes` the reply holds every design for the name, on every shape, so the shape
 * is a way of READING that reply rather than part of what was asked for. Keying the cache on it
 * made each shape change a miss, and every switch showed a spinner for designs already in memory.
 *
 * Dropping it from the key is what makes one lookup serve the whole step. Every other field keeps
 * today's key, shape included.
 */
function designLibraryCacheKey(orderProps, settings) {
  if (!settings || settings.show_other_shapes !== true) return orderPropsKey(orderProps);
  var shapeField = settings.shape_field || DEFAULT_SHAPE_FIELD;
  var target = String(shapeField).trim().toLowerCase();
  var reduced = {};
  Object.keys(orderProps || {}).forEach(function (k) {
    if (String(k).trim().toLowerCase() === target) return;
    reduced[k] = orderProps[k];
  });
  return orderPropsKey(reduced);
}

/**
 * MNGOPT-10 — read one cached reply as if it had been fetched for `chosenShape`.
 *
 * The server already stamped every design with its shape, so switching shape is a re-read of what
 * is in hand, not a new question. Returns a response shaped exactly like a fresh one so every
 * caller downstream — the cap, the picker, the cart — cannot tell the difference.
 */
function reshapeResponseForShape(response, chosenShape, settings) {
  if (!response || response.ok === false) return response;
  var all = Array.isArray(response.allShapeSummaries) ? response.allShapeSummaries : null;
  /* No all-shapes payload means an older reply (or a field that did not opt in) — leave it be. */
  if (!all) return response;

  /* The shopper's shape comes from the field (`Diamond (Popular)`), the rows from the catalog
     (`diamond`). Comparing them raw put every design on the wrong side of the split. */
  var chosen = canonicalShapeName(chosenShape);
  var mine = [];
  var others = [];
  var seen = {};
  for (var i = 0; i < all.length; i++) {
    var row = all[i];
    if (!row) continue;
    var shape = canonicalShapeName(row.shape);
    if (!shape) continue;
    if (shape === chosen) { mine.push(row); continue; }
    if (seen[shape]) continue;
    seen[shape] = true;
    others.push(row);
  }

  var out = {};
  for (var k in response) { if (Object.prototype.hasOwnProperty.call(response, k)) out[k] = response[k]; }
  out.matchSummaries = mine;
  out.totalMatches = mine.length;
  out.otherShapeSummaries = others;
  return out;
}

/**
 * MNGSD-2 D3 — never trade a match line that knows the design's shape for one that does not.
 *
 * After a page reload the reply is gone, so a fresh line can only say `name=joshua`; the line the
 * cart already carried said `name=joshua;shape=hexagon`. Same design, less information — keep
 * the one that knows. A different name, or a line that now knows MORE, wins as usual. The caller
 * makes sure both lines describe the same design (same entry id) before asking.
 */
function mergeDesignMatch(previous, next) {
  var prev = String(previous == null ? '' : previous).trim();
  var cur = String(next == null ? '' : next).trim();
  if (!prev) return cur;
  if (!cur) return prev;
  var prevHasShape = /(^|;)shape=/.test(prev);
  var curHasShape = /(^|;)shape=/.test(cur);
  if (curHasShape || !prevHasShape) return cur;
  var prevName = (prev.match(/(^|;)name=([^;]*)/) || [])[2] || '';
  var curName = (cur.match(/(^|;)name=([^;]*)/) || [])[2] || '';
  return prevName === curName ? prev : cur;
}

/**
 * MNGSD-2 D2 — the selection that survives a shape change. Follow, or fall back; never empty.
 *
 * `summaries` are the designs on the shape the shopper has NOW. Before this rule, a design picked
 * under one shape rode through a shape change untouched — it was still in the grid as an
 * "other shape" card, and the restore found it by id — and PP40377 shipped a Round pendant against
 * a Hexagon order. This replaces PB-DL locked decision 10 ("shape changed → clear it"), which left
 * the step empty and the cart props stale.
 *
 * | before        | this shape has the name | after                      |
 * |---------------|-------------------------|----------------------------|
 * | a design      | yes, that one           | kept                       |
 * | a design      | yes, others             | the newest on this shape   |
 * | a design      | no                      | Original Design (fallback) |
 * | Original      | any                     | Original — they chose it   |
 * | nothing       | any                     | nothing — step not answered|
 */
function resolveSelectionForShape(selection, summaries, options) {
  var mode = selection && selection.mode;
  var mine = Array.isArray(summaries) ? summaries : [];
  if (mode === 'new_design') {
    /* Original Design the shopper CHOSE is kept. Original Design the fallback put there — the
       shape they were on had no design — is not a choice: when they come back to a shape that
       has one, they get it back. Without this the bounce Hexagon → Square → Hexagon left the
       design sitting unselected and the line reading Original Design. */
    if (selection.origin === 'fallback') {
      for (var r = 0; r < mine.length; r++) {
        if (mine[r] && mine[r].entryId && (mine[r].previewUrl || mine[r].productionUrl)) {
          return { mode: 'catalog', entryId: String(mine[r].entryId), outcome: 'recovered' };
        }
      }
    }
    return { mode: 'new_design', entryId: null, outcome: 'kept' };
  }
  if (mode !== 'catalog') return { mode: null, entryId: null, outcome: 'none' };
  var id = selection.entryId != null ? String(selection.entryId) : '';
  for (var i = 0; i < mine.length; i++) {
    if (mine[i] && id && String(mine[i].entryId || '') === id) {
      return { mode: 'catalog', entryId: id, outcome: 'kept' };
    }
  }
  /* Unknown is not wrong. Moving a design takes EVIDENCE that it is on another shape — the
     reply placing it there, and the shopper's shape known. A retired design, or one the reply
     cannot place, is left exactly as the shopper (or their saved cart line) had it. */
  var held = canonicalShapeName(options && options.heldShape);
  var chosen = canonicalShapeName(options && options.chosenShape);
  if (!held || !chosen || held === chosen) return { mode: 'catalog', entryId: id, outcome: 'kept' };
  /* Follow only to a design that can be shown and sold — an id with no picture would write a
     line with no design at all. */
  for (var j = 0; j < mine.length; j++) {
    if (mine[j] && mine[j].entryId && (mine[j].previewUrl || mine[j].productionUrl)) {
      return { mode: 'catalog', entryId: String(mine[j].entryId), outcome: 'followed' };
    }
  }
  return { mode: 'new_design', entryId: null, outcome: 'fallback' };
}

/**
 * MNGSD-2 D4 — the designs on the shopper's CURRENT shape, read from the reply already in hand.
 * No request: the reply carries every shape (MNGOPT-9). A reply with no shapes (an older reply,
 * a family with no shape slot) is its own answer, so those families see no change.
 */
function summariesForChosenShape(response, chosenShape, settings) {
  if (!response) return [];
  var shaped = canonicalShapeName(chosenShape)
    ? reshapeResponseForShape(response, chosenShape, settings)
    : response;
  return Array.isArray(shaped && shaped.matchSummaries) ? shaped.matchSummaries : [];
}

/**
 * MNGSD-2 — the shape a design is KNOWN to be on, or '' when the reply does not say.
 *
 * The server stamps `shape` on the all-shapes and other-shape rows only; `matchSummaries` rows
 * carry none. So the design's own shape is read from the stamped rows, never inferred from the
 * shape that was asked for.
 */
function knownDesignShape(response, entryId) {
  if (!response || entryId == null || entryId === '') return '';
  var id = String(entryId);
  var lists = [response.allShapeSummaries, response.otherShapeSummaries, response.matchSummaries];
  for (var l = 0; l < lists.length; l++) {
    var list = lists[l];
    if (!Array.isArray(list)) continue;
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item || String(item.entryId || '') !== id) continue;
      var shape = canonicalShapeName(item.shape);
      if (shape) return shape;
    }
  }
  return '';
}

function findMatchSummary(response, entryId) {
  if (!response || !entryId) return null;
  var id = String(entryId);
  /* MNGOPT-5 — search the other-shape designs too. Picking one must resolve to its FULL summary
     straight away, production files included: falling back to the card's markup would put a
     design in the cart with no print file if the shopper hit Continue before the re-lookup. */
  /* MNGSD-2 — and the all-shapes rows (MNGOPT-9): a design on a hand-switched shape that is not
     that shape's newest lives only there, and it still needs its production files in the cart. */
  var lists = [response.matchSummaries, response.otherShapeSummaries, response.allShapeSummaries];
  for (var l = 0; l < lists.length; l++) {
    var list = lists[l];
    if (!Array.isArray(list)) continue;
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (item && String(item.entryId || '') === id) return item;
    }
  }
  return null;
}

function normalizeDesignMediaUrl(url) {
  if (url == null || url === '') return '';
  return String(url).trim().split('?')[0].toLowerCase();
}

function findMatchSummaryByPreviewUrl(response, previewUrl) {
  if (!previewUrl || !response || !Array.isArray(response.matchSummaries)) return null;
  var target = normalizeDesignMediaUrl(previewUrl);
  if (!target) return null;
  for (var i = 0; i < response.matchSummaries.length; i++) {
    var item = response.matchSummaries[i];
    if (!item) continue;
    if (normalizeDesignMediaUrl(item.previewUrl) === target) return item;
    if (normalizeDesignMediaUrl(item.productionUrl) === target) return item;
  }
  return null;
}

function findDesignIndexEntryByPreviewUrl(index, previewUrl) {
  if (!previewUrl || !Array.isArray(index)) return null;
  var target = normalizeDesignMediaUrl(previewUrl);
  if (!target) return null;
  for (var i = 0; i < index.length; i++) {
    var entry = index[i];
    if (!entry) continue;
    if (normalizeDesignMediaUrl(entry.preview_image) === target) return entry;
  }
  return null;
}

/**
 * Tier 1 cart props often store Design File URL only (no entry id).
 * Resolve catalog entry id from lookup response and/or static designLibraryIndex.
 */
function resolveCatalogEntryFromSnapshot(snapshot, response, index) {
  if (!snapshot || snapshot.mode !== 'catalog') return snapshot;
  if (snapshot.entryId != null && String(snapshot.entryId).trim() !== '') return snapshot;

  var previewUrl = snapshot.previewUrl || '';
  if (!previewUrl) return snapshot;

  var fromResponse = findMatchSummaryByPreviewUrl(response, previewUrl);
  if (fromResponse && fromResponse.entryId != null && String(fromResponse.entryId).trim() !== '') {
    return {
      mode: 'catalog',
      entryId: String(fromResponse.entryId),
      previewUrl: fromResponse.previewUrl || snapshot.previewUrl,
      productionUrl: fromResponse.productionUrl || snapshot.productionUrl || '',
      designSource: snapshot.designSource
    };
  }

  var fromIndex = findDesignIndexEntryByPreviewUrl(index, previewUrl);
  if (fromIndex && fromIndex.id != null && String(fromIndex.id).trim() !== '') {
    return {
      mode: 'catalog',
      entryId: String(fromIndex.id),
      previewUrl: fromIndex.preview_image || snapshot.previewUrl,
      productionUrl: snapshot.productionUrl || '',
      designSource: snapshot.designSource
    };
  }

  return snapshot;
}

/** A card offering a design from a shape the shopper did not pick (MNGOPT-2). */
function isOtherShapeCard(card) {
  return !!(card && card.classList && card.classList.contains('pb-design-card--other-shape'));
}

function selectDesignCardInGrid(grid, snapshot, response) {
  if (!grid || !snapshot || snapshot.mode !== 'catalog') return null;
  var resolved = resolveCatalogEntryFromSnapshot(snapshot, response, null);
  var entryId = resolved.entryId;
  var previewUrl = resolved.previewUrl || snapshot.previewUrl || '';

  grid.querySelectorAll('.pb-design-card').forEach(function (el) { el.classList.remove('selected'); });

  /* MNGSD-2 — an other-shape card carries the same id and preview as the design it offers, so
     restoring onto one is how a saved design rode through a shape change with the wrong shape
     (PP40377). Only the shopper's own shape's cards may be restored onto. */
  var cards = grid.querySelectorAll('.pb-design-card--catalog');
  if (entryId) {
    for (var b = 0; b < cards.length; b++) {
      var candidate = cards[b];
      if (isOtherShapeCard(candidate)) continue;
      if (String(candidate.getAttribute('data-entry-id') || '') !== String(entryId)) continue;
      candidate.classList.add('selected');
      return candidate;
    }
  }

  if (previewUrl) {
    var target = normalizeDesignMediaUrl(previewUrl);
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (isOtherShapeCard(card)) continue;
      var cardUrl = card.getAttribute('data-preview-url') || '';
      if (!cardUrl) continue;
      if (normalizeDesignMediaUrl(cardUrl) === target) {
        card.classList.add('selected');
        return card;
      }
    }
  }

  return null;
}

function clearDesignLibraryCartProps(target, fieldLabel) {
  var out = target || {};
  DESIGN_LIBRARY_CART_KEYS.forEach(function (key) {
    delete out[key];
  });
  var label = fieldLabel ? String(fieldLabel).trim() : '';
  if (label) delete out[label];
  return out;
}

function designLibraryVisibleCartValue(summary) {
  if (!summary) return '';
  if (summary.previewUrl) return String(summary.previewUrl);
  if (summary.productionUrl) return String(summary.productionUrl);
  return '';
}

function buildCatalogCartProps(summary, options) {
  options = options || {};
  var fieldLabel = options.fieldLabel ? String(options.fieldLabel).trim() : '';
  var props = {};
  if (!summary || !fieldLabel) return props;
  var visibleUrl = designLibraryVisibleCartValue(summary);
  if (!visibleUrl) return props;
  props[fieldLabel] = visibleUrl;
  if (summary.entryId != null && String(summary.entryId).trim() !== '') {
    props[CART_KEY_ENTRY_ID] = String(summary.entryId);
  }
  if (summary.previewUrl && String(summary.previewUrl) !== visibleUrl) {
    props[CART_KEY_PREVIEW_URL] = String(summary.previewUrl);
  }
  if (summary.productionUrl) {
    props[CART_KEY_PRODUCTION_URL] = String(summary.productionUrl);
  }
  if (Array.isArray(summary.productionFiles) && summary.productionFiles.length) {
    var productionFilesBlock = formatProductionFilesBlock(summary.productionFiles);
    if (productionFilesBlock) props[CART_KEY_PRODUCTION_FILES] = productionFilesBlock;
  }
  if (options.designMatch) props[CART_KEY_MATCH] = String(options.designMatch);
  return props;
}

function buildNewDesignCartProps(options) {
  options = options || {};
  var fieldLabel = options.fieldLabel ? String(options.fieldLabel).trim() : '';
  var newDesignLabel = options.newDesignLabel || DEFAULT_NEW_DESIGN_LABEL;
  var props = {};
  if (!fieldLabel) return props;
  props[fieldLabel] = newDesignLabel;
  return props;
}

function buildCartPropsFromSelection(selection) {
  if (!selection || !selection.mode) return {};
  var fieldLabel = selection.fieldLabel ? String(selection.fieldLabel).trim() : '';
  var newDesignLabel = selection.newDesignLabel || DEFAULT_NEW_DESIGN_LABEL;
  if (selection.mode === 'new_design') {
    return buildNewDesignCartProps({ fieldLabel: fieldLabel, newDesignLabel: newDesignLabel });
  }
  if (selection.mode === 'catalog') {
    var summary = selection.summary;
    if (!summary && selection.response && selection.entryId) {
      summary = findMatchSummary(selection.response, selection.entryId);
    }
    if (!summary && selection.entryId) {
      summary = {
        entryId: selection.entryId,
        previewUrl: selection.previewUrl || '',
        productionUrl: selection.productionUrl || ''
      };
    }
    return buildCatalogCartProps(summary, { fieldLabel: fieldLabel, designMatch: selection.designMatch });
  }
  return {};
}

function isDesignLibraryStepComplete(fieldState, options) {
  options = options || {};
  if (!fieldState) return false;
  if (fieldState.skipped) return true;
  if (fieldState.loading) return false;
  var hasPick = fieldState.selectionMode === 'catalog' || fieldState.selectionMode === 'new_design' || fieldState.selectionMode === 'scratch';
  if (hasPick && fieldState.shown) return true;
  if (hasPick && options.persistedPick === true) return true;
  if (!fieldState.shown) return false;
  return hasPick;
}

function resolveDesignDisplayImage(source, options) {
  options = options || {};
  var fallback = source && source.fallbackImage != null
    ? String(source.fallbackImage)
    : (options.fallbackImage != null ? String(options.fallbackImage) : '');
  var props = source && source.properties
    ? source.properties
    : (source && typeof source === 'object' ? source : {});
  var snapshot = parseDesignLibraryCartSnapshot(props);
  if (snapshot.mode === 'catalog' && snapshot.previewUrl) {
    return {
      url: String(snapshot.previewUrl),
      mode: 'catalog',
      usedDesign: true
    };
  }
  var aiResolver = typeof globalThis !== 'undefined' && globalThis.PBCartDisplayImage
    ? globalThis.PBCartDisplayImage.resolveCartLineDisplayImageUrl
    : null;
  if (typeof aiResolver === 'function') {
    var aiUrl = aiResolver(props, {
      fallbackImage: '',
      fieldConfig: options.fieldConfig || null
    });
    if (aiUrl) {
      return {
        url: String(aiUrl),
        mode: 'ai',
        usedDesign: false
      };
    }
  }
  return {
    url: fallback,
    mode: snapshot.mode || 'none',
    usedDesign: false
  };
}

function resolveDesignDisplayImageUrl(propsOrSelections, fallbackImage, fieldConfig) {
  return resolveDesignDisplayImage({
    properties: propsOrSelections,
    fallbackImage: fallbackImage
  }, { fieldConfig: fieldConfig || null }).url;
}

function parseDesignLibraryCartSnapshot(props, options) {
  options = options || {};
  var raw = props && typeof props === 'object' ? props : {};
  var fieldLabels = Array.isArray(options.fieldLabels) ? options.fieldLabels : [];
  var newDesignLabels = options.newDesignLabels && typeof options.newDesignLabels === 'object'
    ? options.newDesignLabels
    : {};

  if (fieldLabels.length) {
    for (var vi = 0; vi < fieldLabels.length; vi += 1) {
      var vLabel = fieldLabels[vi];
      var vVal = raw[vLabel];
      if (vVal == null || String(vVal).trim() === '') continue;
      var vStr = String(vVal).trim();
      if (/^https?:\/\//i.test(vStr)) {
        return {
          mode: 'catalog',
          fieldLabel: vLabel,
          entryId: raw[CART_KEY_ENTRY_ID] != null ? String(raw[CART_KEY_ENTRY_ID]) : null,
          previewUrl: raw[CART_KEY_PREVIEW_URL] ? String(raw[CART_KEY_PREVIEW_URL]) : vStr,
          productionUrl: raw[CART_KEY_PRODUCTION_URL] ? String(raw[CART_KEY_PRODUCTION_URL]) : '',
          designMatch: raw[CART_KEY_MATCH] ? String(raw[CART_KEY_MATCH]) : '',
          designSource: DESIGN_SOURCE_CATALOG
        };
      }
      var vNewDesign = newDesignLabels[vLabel] || DEFAULT_NEW_DESIGN_LABEL;
      if (vStr === vNewDesign || vStr === DESIGN_SOURCE_SCRATCH || !/^https?:\/\//i.test(vStr)) {
        return {
          mode: 'new_design',
          fieldLabel: vLabel,
          entryId: null,
          previewUrl: '',
          productionUrl: '',
          designSource: DESIGN_SOURCE_SCRATCH,
          savedLabelText: vStr
        };
      }
    }
  }

  var designSource = raw[DESIGN_SOURCE_KEY];
  var entryId = raw[CART_KEY_ENTRY_ID];
  var previewUrl = raw[CART_KEY_PREVIEW_URL];
  var productionUrl = raw[CART_KEY_PRODUCTION_URL];
  var designFile = raw[DESIGN_FILE_KEY];

  if (designSource === DESIGN_SOURCE_SCRATCH) {
    return {
      mode: 'new_design',
      fieldLabel: null,
      entryId: null,
      previewUrl: '',
      productionUrl: '',
      designSource: designSource,
      savedLabelText: DESIGN_SOURCE_SCRATCH
    };
  }

  if (entryId != null && String(entryId).trim() !== '') {
    return {
      mode: 'catalog',
      fieldLabel: null,
      entryId: String(entryId),
      previewUrl: previewUrl ? String(previewUrl) : (designFile ? String(designFile) : ''),
      productionUrl: productionUrl ? String(productionUrl) : '',
      designMatch: raw[CART_KEY_MATCH] ? String(raw[CART_KEY_MATCH]) : '',
      designSource: designSource || DESIGN_SOURCE_CATALOG
    };
  }

  if (designFile != null && String(designFile).trim() !== '') {
    return {
      mode: 'catalog',
      fieldLabel: null,
      entryId: null,
      previewUrl: String(designFile).trim(),
      productionUrl: '',
      designSource: designSource || DESIGN_SOURCE_CATALOG
    };
  }

  if (designSource === DESIGN_SOURCE_CATALOG) {
    return {
      mode: 'catalog',
      fieldLabel: null,
      entryId: null,
      previewUrl: '',
      productionUrl: '',
      designSource: designSource
    };
  }

  return {
    mode: null,
    fieldLabel: null,
    entryId: null,
    previewUrl: '',
    productionUrl: '',
    designSource: undefined
  };
}

function mergeDesignLibraryCartPropsIntoSelections(props, selections, options) {
  options = options || {};
  var out = selections && typeof selections === 'object' ? Object.assign({}, selections) : {};
  var snapshot = parseDesignLibraryCartSnapshot(props, options);
  DESIGN_LIBRARY_CART_KEYS.forEach(function(key) {
    delete out[key];
  });
  delete out[DESIGN_SOURCE_KEY];
  delete out[DESIGN_FILE_KEY];
  if (Array.isArray(options.fieldLabels)) {
    options.fieldLabels.forEach(function(label) {
      delete out[label];
    });
  }
  if (snapshot.fieldLabel) delete out[snapshot.fieldLabel];

  var fieldLabel = snapshot.fieldLabel || options.defaultFieldLabel || '';
  if (snapshot.mode === 'new_design') {
    if (fieldLabel) {
      var scratch = (options.newDesignLabels && options.newDesignLabels[fieldLabel]) || DEFAULT_NEW_DESIGN_LABEL;
      out[fieldLabel] = scratch;
    }
    return out;
  }
  if (snapshot.mode === 'catalog') {
    var visible = designLibraryVisibleCartValue({
      previewUrl: snapshot.previewUrl,
      productionUrl: snapshot.productionUrl
    });
    if (fieldLabel && visible) out[fieldLabel] = visible;
    if (snapshot.designMatch) out[CART_KEY_MATCH] = snapshot.designMatch;
  }
  return out;
}

var api = {
  DEBOUNCE_MS: DEBOUNCE_MS,
  LOOKUP_MAX_RETRIES: LOOKUP_MAX_RETRIES,
  LOOKUP_RETRY_BASE_MS: LOOKUP_RETRY_BASE_MS,
  DEFAULT_LAYOUT: DEFAULT_LAYOUT,
  DEFAULT_NEW_DESIGN_LABEL: DEFAULT_NEW_DESIGN_LABEL,
  DEFAULT_NEW_DESIGN_PREVIEW: DEFAULT_NEW_DESIGN_PREVIEW,
  DEFAULT_NEW_DESIGN_PREVIEW_MSG: DEFAULT_NEW_DESIGN_PREVIEW_MSG,
  DEFAULT_NEW_DESIGN_WHEN: DEFAULT_NEW_DESIGN_WHEN,
  resolveNewDesignPreviewCopy: resolveNewDesignPreviewCopy,
  DESIGN_SOURCE_KEY: DESIGN_SOURCE_KEY,
  DESIGN_SOURCE_SCRATCH: DESIGN_SOURCE_SCRATCH,
  DESIGN_SOURCE_CATALOG: DESIGN_SOURCE_CATALOG,
  DESIGN_FILE_KEY: DESIGN_FILE_KEY,
  CART_KEY_ENTRY_ID: CART_KEY_ENTRY_ID,
  CART_KEY_PREVIEW_URL: CART_KEY_PREVIEW_URL,
  CART_KEY_PRODUCTION_URL: CART_KEY_PRODUCTION_URL,
  CART_KEY_PRODUCTION_FILES: CART_KEY_PRODUCTION_FILES,
  CART_KEY_MATCH: CART_KEY_MATCH,
  DESIGN_LIBRARY_CART_KEYS: DESIGN_LIBRARY_CART_KEYS,
  parseDefaultOrderProps: parseDefaultOrderProps,
  parseDesignLibrarySettings: parseDesignLibrarySettings,
  normalizeDesignLibraryLayout: normalizeDesignLibraryLayout,
  buildOrderProps: buildOrderProps,
  buildLookupRequest: buildLookupRequest,
  resolveProductionPageLabels: resolveProductionPageLabels,
  formatProductionFilesBlock: formatProductionFilesBlock,
  DEFAULT_PROXY_PATH: DEFAULT_PROXY_PATH,
  normalizeProxyPath: normalizeProxyPath,
  shouldUseMockDesignLibrary: shouldUseMockDesignLibrary,
  parseResolveResponseBody: parseResolveResponseBody,
  decideStepVisibility: decideStepVisibility,
  isScratchOnlyResponse: isScratchOnlyResponse,
  shouldShowNewDesign: shouldShowNewDesign,
  shouldShowCatalogPicker: shouldShowCatalogPicker,
  getCatalogMatchCount: getCatalogMatchCount,
  shouldAutoSwitchCartEditOnDepsChange: shouldAutoSwitchCartEditOnDepsChange,
  shouldPreserveCartEditSelectionOnLookup: shouldPreserveCartEditSelectionOnLookup,
  resolveNewDesignLabelForRestore: resolveNewDesignLabelForRestore,
  resolveDesignLibrary: resolveDesignLibrary,
  resolveDesignLibraryWithRetry: resolveDesignLibraryWithRetry,
  mockResolveDesignLibrary: mockResolveDesignLibrary,
  cancelInFlightLookup: cancelInFlightLookup,
  isAbortError: isAbortError,
  isLookupCancellationError: isLookupCancellationError,
  isExpectedLookupAbort: isExpectedLookupAbort,
  beginLookupGeneration: beginLookupGeneration,
  isStaleLookupGeneration: isStaleLookupGeneration,
  hasCachedLookupResult: hasCachedLookupResult,
  needsDesignLibraryLookup: needsDesignLibraryLookup,
  shouldScheduleDesignLibraryLookup: shouldScheduleDesignLibraryLookup,
  shouldScheduleDesignLibraryPrefetch: shouldScheduleDesignLibraryPrefetch,
  isTransientLookupFailure: isTransientLookupFailure,
  renderScratchThumbnail: renderScratchThumbnail,
  renderCatalogThumbnail: renderCatalogThumbnail,
  renderThumbnailGrid: renderThumbnailGrid,
  getFirstMatchSummary: getFirstMatchSummary,
  orderPropsKey: orderPropsKey,
  findMatchSummary: findMatchSummary,
  findMatchSummaryByPreviewUrl: findMatchSummaryByPreviewUrl,
  findDesignIndexEntryByPreviewUrl: findDesignIndexEntryByPreviewUrl,
  resolveCatalogEntryFromSnapshot: resolveCatalogEntryFromSnapshot,
  selectDesignCardInGrid: selectDesignCardInGrid,
  normalizeDesignMediaUrl: normalizeDesignMediaUrl,
  clearDesignLibraryCartProps: clearDesignLibraryCartProps,
  designLibraryVisibleCartValue: designLibraryVisibleCartValue,
  buildCatalogCartProps: buildCatalogCartProps,
  buildNewDesignCartProps: buildNewDesignCartProps,
  buildCartPropsFromSelection: buildCartPropsFromSelection,
  isDesignLibraryStepComplete: isDesignLibraryStepComplete,
  parseDesignLibraryCartSnapshot: parseDesignLibraryCartSnapshot,
  resolveDesignDisplayImage: resolveDesignDisplayImage,
  resolveDesignDisplayImageUrl: resolveDesignDisplayImageUrl,
  mergeDesignLibraryCartPropsIntoSelections: mergeDesignLibraryCartPropsIntoSelections,
  escapeAttr: escapeAttr,
  parseMaxDesigns: parseMaxDesigns,
  applyDesignLibraryDisplayCap: applyDesignLibraryDisplayCap,
  selectOtherShapeOptions: selectOtherShapeOptions,
  hasOtherShapeOptions: hasOtherShapeOptions,
  formatShapeLabel: formatShapeLabel,
  renderOtherShapesLabel: renderOtherShapesLabel,
  shapeMatchesOptionValue: shapeMatchesOptionValue,
  canonicalShapeName: canonicalShapeName,
  designLibraryCacheKey: designLibraryCacheKey,
  reshapeResponseForShape: reshapeResponseForShape,
  resolveSelectionForShape: resolveSelectionForShape,
  summariesForChosenShape: summariesForChosenShape,
  knownDesignShape: knownDesignShape,
  formatDesignMatch: formatDesignMatch,
  mergeDesignMatch: mergeDesignMatch,
  OTHER_SHAPES_MAX: OTHER_SHAPES_MAX
};

if (typeof globalThis !== 'undefined') {
  globalThis.PBDesignLibraryLogic = api;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = api;
}
